import React from 'react'
import { DecodedValueMap, useQueryParams } from 'use-query-params'
import { PredefinedDateRangeItem } from '../../../components/utils/predefinedDateRanges'
import dayjs from 'shared/dayjs'
import {
  DateFilterType,
  QueryFilterConfigType,
  SetDateFilterQuery
} from './dateFilter.types'
import { QUERY_FILTER_PARAMS_CONFIG } from './constants'

type Nullable<T> = {
  [key in keyof T]?: T[key] | null
}

type DecodedQueryFilterType = DecodedValueMap<QueryFilterConfigType>

const normalizeFilterFromQuery = (
  filter: DecodedQueryFilterType,
  defaultValues: Nullable<DateFilterType> = {}
): DateFilterType => {
  return applyPeriodToInterval(filter, defaultValues)
}

const applyPeriodToInterval = (
  query: Nullable<DateFilterType>,
  defaultValues: Nullable<DateFilterType> = {}
): DateFilterType => {
  const periodFromQuery = query.period
  let from: Date
  let to: Date
  let period: PredefinedDateRangeItem | null = null

  if (!periodFromQuery) {
    // use default period only if "from"&"to" are empty
    if (!query.to && !query.from) {
      period = defaultValues.period || null
    }
  } else {
    period = periodFromQuery
  }

  if (period) {
    from = dayjs().subtract(period.amount, period.unit).startOf('day').toDate()
    to = dayjs().endOf('day').toDate()
  } else {
    from = dayjs(query.from || defaultValues.from)
      .startOf('day')
      .toDate()
    to = dayjs(query.to || defaultValues.to)
      .endOf('day')
      .toDate()
  }

  return {
    period,
    from,
    to
  }
}

const transformFiltersToQueryStringTypes = (
  filterParams: Nullable<DateFilterType>
) => ({
  // store only relative period instead of absolute values
  from: filterParams.period ? undefined : filterParams.from,
  to: filterParams.period ? undefined : filterParams.to,
  period: filterParams.period
})

export const useDateFilterFromQuery = (
  defaultValues?: Partial<DateFilterType>
): [DateFilterType, SetDateFilterQuery] => {
  const [filterFromQuery, setFilter] = useQueryParams(
    QUERY_FILTER_PARAMS_CONFIG
  )

  const filter = React.useMemo(() => {
    return normalizeFilterFromQuery(filterFromQuery, defaultValues)
  }, [filterFromQuery])

  const changeFilter: SetDateFilterQuery = React.useCallback((changes) => {
    setFilter((currentFilters) => {
      const patch =
        typeof changes === 'function' ? changes(currentFilters) : changes

      return transformFiltersToQueryStringTypes({
        ...normalizeFilterFromQuery(currentFilters, defaultValues),
        ...patch
      })
    })
  }, [])

  return [filter, changeFilter]
}
